import React, { useState, useEffect, useRef } from "react";
import { BUILDINGS } from "@/config/constants";
import Text from "@/components/Text";
import { useTranslation } from "react-i18next";

/**
 * マップ上に表示される階層セレクタボタン（ドロップダウン機能付き）
 * sm以上のビューで、マップの右上に固定位置で表示される
 */
interface MapFloorSelectorButtonSmProps {
  selectedBuilding: string | null;
  selectedFloor: number | null;
  onFloorChange: (buildingId: string, floor: number) => Promise<void>;
  className?: string;
}

const MapFloorSelectorButtonSm: React.FC<MapFloorSelectorButtonSmProps> = ({
  selectedBuilding,
  selectedFloor,
  onFloorChange,
  className = "",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * ドロップダウン外側のクリックを検出して自動的に閉じる
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  /**
   * 階層選択時の処理
   */
  const handleFloorClick = async (buildingId: string, floor: number) => {
    await onFloorChange(buildingId, floor);
    setIsOpen(false);
  };

  /**
   * ボタンを統一スタイルでレンダリング
   */
  const renderFloorButton = (floor: number, buildingId: string, isSelected: boolean) => {
    const baseClasses = [
      "flex",
      "items-center",
      "justify-center",
      "rounded-2xl",
      "border",
      "w-[72px]",
      "h-[72px]",
      "border-Border",
      "transition-colors",
      "duration-150",
      "ease-in-out",
    ];

    const colorClasses = isSelected
      ? ["bg-MiraiPrimary", "text-white"]
      : ["bg-white", "text-TextPrimary", "hover:bg-Background"];

    const classes = [...baseClasses, ...colorClasses].join(" ");

    return (
      <button
        key={floor}
        className={classes}
        onClick={() => handleFloorClick(buildingId, floor)}
        aria-pressed={isSelected}
        aria-label={`${floor}階`}
      >
        <Text
          as="span"
          variant="body/l"
          weight="bold"
          style={isSelected ? { color: "#fff" } : undefined}
        >
          {floor}
        </Text>
      </button>
    );
  };

  return (
    <div ref={containerRef} className={className}>
      {/* トリガーボタン */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-[72px] w-[72px] flex-col items-center justify-center gap-0.5 rounded-2xl border border-Border bg-MiraiPrimary text-white transition-colors duration-150 ease-in-out hover:bg-Background"
      >
        {selectedBuilding && (
          <span className="text-[10px] font-bold leading-none text-white/80">
            {t(BUILDINGS.find((b) => b.id === selectedBuilding)?.label ?? "")}
          </span>
        )}
        <Text as="span" variant="body/l" weight="bold">
          {selectedFloor}
        </Text>
      </button>

      {/* ドロップダウンメニュー */}
      <div
        className={`
          absolute top-full right-0 mt-2 bg-white border border-Border rounded-2xl shadow-lg
          transition-all duration-200 ease-out origin-top-right
          ${
            isOpen
              ? "opacity-100 scale-y-100 pointer-events-auto"
              : "opacity-0 scale-y-95 pointer-events-none"
          }
        `}
        style={{ transformOrigin: "top right" }}
      >
        <div className="p-5">
          <div className="flex gap-5">
            {BUILDINGS.map((building) => (
              <div key={building.id}>
                <Text
                  as="div"
                  variant="body/s"
                  weight="bold"
                  className="text-TextPrimary mb-3 text-center whitespace-nowrap"
                >
                  {t(building.label)}
                </Text>
                <div className="flex flex-col gap-2">
                  {building.floors.map((floor) => {
                    const isSelected = selectedBuilding === building.id && selectedFloor === floor;

                    return renderFloorButton(floor, building.id, isSelected);
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapFloorSelectorButtonSm;
